import { useId, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { usePreferences } from '@/hooks/usePreferences';
import { Icon } from '@/icons';
import { Button } from '@/ui';

const float = keyframes`0%,100% { transform: translateY(0); } 50% { transform: translateY(-9px); }`;
const glow = keyframes`0%,100% { opacity: .5; } 50% { opacity: 1; }`;
const drift = keyframes`0%,100% { transform: translate(0,0); } 50% { transform: translate(7px,-6px); }`;
const Banner = styled.header`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1.1fr 0.85fr 1.15fr;
  align-items: center;
  gap: 12px;
  min-height: 216px;
  padding: 28px 24px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.lg};
  color: ${({ theme }) => theme.color.textPrimary};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'radial-gradient(ellipse at 48% 42%, #6510b975, transparent 53%), linear-gradient(115deg, #100a24, #170b37 58%, #100e25)'
      : 'radial-gradient(ellipse at 48% 42%, #b767ee55, transparent 53%), linear-gradient(115deg, #fbf7ff, #ebdcff 58%, #f6efff)'};
  h1 {
    margin: 0 0 12px;
    font: 750 clamp(30px, 3vw, 44px)/1.1 ${({ theme }) => theme.font.display};
  }
  p {
    margin: 0;
    font-size: ${({ theme }) => theme.fontSize.sm};
    line-height: 1.6;
    color: ${({ theme }) => theme.color.textMuted};
  }
  @media (max-width: 1200px) {
    grid-template-columns: 1.2fr 0.8fr;
    padding: 22px;
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    padding: 24px 20px;
    gap: 18px;
  }
`;
const Copy = styled.div`
  position: relative;
  z-index: 1;
`;
const Message = styled(Copy)`
  h2 {
    margin: 0 0 12px;
    font-size: ${({ theme }) => theme.fontSize.md};
    line-height: 1.4;
  }
  button {
    margin-top: 16px;
  }
  @media (max-width: 1200px) {
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    h2 {
      margin-bottom: 3px;
    }
    button {
      margin: 0;
      flex-shrink: 0;
    }
  }
  @media (max-width: 600px) {
    display: block;
    padding-top: 12px;
    border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
    button {
      margin-top: 14px;
    }
  }
`;
const ViewAll = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  background: none;
  padding: 0;
  margin-top: 16px;
  cursor: pointer;
  color: ${({ theme }) => theme.color.brandFrom};
  font: 650 14px ${({ theme }) => theme.font.body};
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 5px;
  }
`;
const Art = styled.div<{ $animate: boolean }>`
  position: relative;
  height: 190px;
  min-width: 0;
  pointer-events: none;
  > svg {
    position: absolute;
    inset: -34px -35%;
    width: 170%;
    height: 260px;
  }
  .bulb {
    position: absolute;
    inset: 12px 0 0;
    display: grid;
    place-items: center;
  }
  .bulb svg {
    width: 96px;
    height: 134px;
    overflow: visible;
  }
  .halo {
    position: absolute;
    inset: 24px -15% 0;
    background: radial-gradient(ellipse, #ae3fff88, transparent 65%);
  }
  ${({ $animate }) =>
    $animate &&
    css`
      .bulb {
        animation: ${float} 5.6s ease-in-out infinite;
      }
      .halo {
        animation: ${glow} 4.8s ease-in-out infinite;
      }
      .floating {
        animation: ${drift} 7s ease-in-out infinite;
      }
      .floating.alt {
        animation-direction: reverse;
        animation-delay: -3s;
      }
    `}
  @media (prefers-reduced-motion: reduce) {
    &,
    * {
      animation: none !important;
    }
  }
  @media (max-width: 600px) {
    position: absolute;
    top: 2px;
    right: -20px;
    width: 150px;
    opacity: 0.3;
    z-index: 0;
  }
`;
const MotionButton = styled.button`
  position: absolute;
  bottom: 8px;
  right: 10px;
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  cursor: pointer;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textMuted};
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
  }
`;

export function FeedbackBanner({
  onSubmit,
  onViewAll,
}: {
  onSubmit: () => void;
  onViewAll: () => void;
}) {
  const { prefs } = usePreferences();
  const [paused, setPaused] = useState(false);
  const id = useId().replace(/:/g, '');
  const enabled = prefs.bannerAnimations && !prefs.reduceMotion;
  return (
    <Banner>
      <Copy>
        <h1>Feedback</h1>
        <p>
          Tell us what to build, improve, or fix next
          <br />
          so we can build something better together.
        </p>
        <ViewAll onClick={onViewAll}>
          <Icon name="link" size={17} /> View all feedback <Icon name="arrow-right" size={17} />
        </ViewAll>
      </Copy>
      <Art
        aria-hidden="true"
        $animate={enabled && !paused}
        data-testid="feedback-banner-art"
        data-animated={enabled && !paused}
      >
        <svg viewBox="0 0 360 260" fill="none">
          <defs>
            <linearGradient id={`${id}-tile`} x1="0" y1="0" x2="1" y2="1">
              <stop stopColor="#bc47ff" />
              <stop offset="1" stopColor="#4800b5" />
            </linearGradient>
          </defs>
          <g stroke="#9d3cff" strokeOpacity=".2">
            <path d="M0 130 180 24 360 130 180 236Z M0 65 360 260 M0 195 330 0 M80 0 360 165 M0 260 360 50" />
            <path d="M90 78V184L180 236V130L270 78V184" />
          </g>
          <g fill={`url(#${id}-tile)`}>
            <path d="m89 170 91-52 90 52-90 53Z" opacity=".55" />
            <path d="m89 170 91 53v15l-91-53Z" opacity=".6" />
            <path d="m180 223 90-53v15l-90 53Z" opacity=".35" />
            <path className="floating" d="m60 166 35-20 50 29-35 20Z" />
            <path className="floating alt" d="m222 157 35-20 50 29-35 20Z" />
            <path className="floating" d="m126 44 56-32 29 17-56 32Z" opacity=".6" />
            <rect className="floating alt" x="32" y="65" width="17" height="17" opacity=".3" />
            <rect className="floating" x="298" y="38" width="20" height="20" opacity=".4" />
          </g>
          <g fill="#d6a2ff">
            <circle cx="81" cy="100" r="2" />
            <circle cx="268" cy="96" r="2" />
            <circle cx="303" cy="180" r="1.5" />
          </g>
        </svg>
        <div className="halo" />
        <div className="bulb">
          <svg viewBox="0 0 100 140" fill="none">
            <defs>
              <filter id={`${id}-glow`} x="-100%" y="-100%" width="300%" height="300%">
                <feGaussianBlur stdDeviation="6" />
              </filter>
            </defs>
            <path
              d="M34 102V90c0-10-17-18-17-39a33 33 0 0 1 66 0c0 21-17 29-17 39v12Z"
              fill="#a742ff"
              stroke="#d783ff"
              strokeWidth="8"
              filter={`url(#${id}-glow)`}
            />
            <path
              d="M34 102V90c0-10-17-18-17-39a33 33 0 0 1 66 0c0 21-17 29-17 39v12Z"
              fill="#a64dfb44"
              stroke="#f5d8ff"
              strokeWidth="1.8"
            />
            <path
              d="M45 102V65c0-14-14-14-11-3 2 7 25 7 29 0 5-11-10-11-10 3v37M34 106h32m-32 5h32m-30 5h28m-22 5h16"
              stroke="#f3ccff"
              strokeWidth="1.7"
              strokeLinecap="round"
            />
            <path d="M55 26q13 2 19 15" stroke="white" strokeWidth="4" strokeLinecap="round" />
          </svg>
        </div>
      </Art>
      <Message>
        <div>
          <h2>
            Your feedback
            <br />
            builds a better TruXSocial
          </h2>
          <p>Real people. Real ideas. A stronger community.</p>
        </div>
        <Button variant="primary" onClick={onSubmit}>
          <Icon name="plus" size={16} /> Submit feedback
        </Button>
      </Message>
      {enabled && (
        <MotionButton
          aria-label={paused ? 'Resume banner animation' : 'Pause banner animation'}
          onClick={() => setPaused(!paused)}
        >
          <Icon name={paused ? 'play' : 'pause'} size={13} />
        </MotionButton>
      )}
    </Banner>
  );
}
